import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Inbox, Plus } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../lib/i18n';

const PRIORITY_LABEL = { low: 'Bassa', medium: 'Media', high: 'Alta', urgent: 'Urgente' };
const PRIORITY_COLOR = { low: '#10B981', medium: '#0EA5E9', high: '#F59E0B', urgent: '#EF4444' };

export default function Dashboard() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [reqLoading, setReqLoading] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login'); return; }
    (async () => {
      try {
        const { data } = await api.get('/help-requests/mine');
        setRequests(data);
      } catch {
        setRequests([]);
      } finally {
        setReqLoading(false);
      }
    })();
  }, [user, loading, navigate]);

  if (loading || !user) {
    return <div className="max-w-3xl mx-auto px-6 py-24 text-muted-foreground">{t.common.loading}</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-3">{t.dashboard.title}</p>
      <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight" data-testid="dashboard-heading">
        {t.dashboard.hello}, {user.name.split(' ')[0]}.
      </h1>
      <p className="mt-2 text-muted-foreground">{user.email}</p>

      <div className="mt-16">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-medium tracking-tight">{t.dashboard.quick_actions}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Link to="/settings" data-testid="dash-quick-settings" className="p-5 rounded-2xl border border-border bg-card hover:-translate-y-0.5 transition-all">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Profilo</div>
            <div className="font-medium mt-1">Impostazioni</div>
          </Link>
          <Link to="/help" data-testid="dash-quick-help" className="p-5 rounded-2xl border border-border bg-card hover:-translate-y-0.5 transition-all">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Aiuto</div>
            <div className="font-medium mt-1">{t.help.new_request}</div>
          </Link>
          <Link to="/diary" data-testid="dash-quick-diary" className="p-5 rounded-2xl border border-border bg-card hover:-translate-y-0.5 transition-all">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Privato</div>
            <div className="font-medium mt-1">{t.nav.diary}</div>
          </Link>
          <Link to="/archive" data-testid="dash-quick-archive" className="p-5 rounded-2xl border border-border bg-card hover:-translate-y-0.5 transition-all">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Privato</div>
            <div className="font-medium mt-1">{t.nav.archive}</div>
          </Link>
          <Link to="/collaborators" data-testid="dash-quick-collab" className="p-5 rounded-2xl border border-border bg-card hover:-translate-y-0.5 transition-all">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Contribuisci</div>
            <div className="font-medium mt-1">{t.collaborators.offer_cta}</div>
          </Link>
          <Link to="/testimonials" data-testid="dash-quick-testimonials" className="p-5 rounded-2xl border border-border bg-card hover:-translate-y-0.5 transition-all">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Condividi</div>
            <div className="font-medium mt-1">{t.testimonials.add}</div>
          </Link>
          <Link to="/assistant" data-testid="dash-quick-assistant" className="p-5 rounded-2xl border border-border bg-card hover:-translate-y-0.5 transition-all">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">AI</div>
            <div className="font-medium mt-1">{t.nav.assistant}</div>
          </Link>
          <Link to="/library" data-testid="dash-quick-library" className="p-5 rounded-2xl border border-border bg-card hover:-translate-y-0.5 transition-all">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Impara</div>
            <div className="font-medium mt-1">{t.nav.library}</div>
          </Link>
        </div>
      </div>

      <div className="mt-16">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-medium tracking-tight">{t.dashboard.my_requests}</h2>
          <Link to="/help" data-testid="dashboard-new-request" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90">
            <Plus size={16} /> {t.help.new_request}
          </Link>
        </div>

        {reqLoading ? (
          <p className="text-muted-foreground">{t.common.loading}</p>
        ) : requests.length === 0 ? (
          <div className="p-12 rounded-3xl border border-dashed border-border text-center" data-testid="no-requests">
            <Inbox size={32} className="mx-auto text-muted-foreground mb-4" strokeWidth={1.5} />
            <p className="text-muted-foreground max-w-md mx-auto">{t.dashboard.no_requests}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((r) => (
              <div key={r.id} data-testid={`request-${r.id}`} className="p-6 rounded-2xl border border-border bg-card flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs px-2.5 py-0.5 rounded-full font-medium" style={{ color: PRIORITY_COLOR[r.priority], backgroundColor: `${PRIORITY_COLOR[r.priority]}15` }}>
                      {PRIORITY_LABEL[r.priority] || r.priority}
                    </span>
                    <span className="text-xs uppercase tracking-widest text-muted-foreground">{r.problem_type}</span>
                  </div>
                  <h3 className="font-medium text-lg">{r.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{r.description}</p>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">{t.dashboard.status}</div>
                  <div className="mt-1 font-medium text-sm">{r.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
